import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import { Layout } from "@/components/Layout";
import { Loader2 } from "lucide-react";

import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import AddExpense from "@/pages/AddExpense";
import Receipts from "@/pages/Receipts";
import Notifications from "@/pages/Notifications";
import Balance from "@/pages/Balance";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";

const Splash = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-7 w-7 animate-spin text-primary" />
  </div>
);

function RequireUser({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireHousehold() {
  const { user, household, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (!household) return <Navigate to="/onboarding" replace />;
  return (
    <RealtimeProvider>
      <Layout>
        <Outlet />
      </Layout>
    </RealtimeProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<RequireUser><Onboarding /></RequireUser>} />
      <Route element={<RequireHousehold />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/add" element={<AddExpense />} />
        <Route path="/receipts" element={<Receipts />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/balance" element={<Balance />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
